import { ref, computed } from 'vue'
import { SORT_DIRECTIONS } from '../utils/adminCenterTableSort'

const PAGE_SIZE = 10

/**
 * Sorts a full list and shows it in batches of 10.
 * A new column starts ascending; clicking the active column flips direction.
 * The number of rows on screen stays as it is: sorting shows that many rows of
 * the new order, and a replaced list shows that many rows of the updated data.
 */
export function useSortedBatch(rows, sortRows, defaultSort) {
  const sort = ref({ key: defaultSort.key, direction: defaultSort.direction })
  const visibleCount = ref(PAGE_SIZE)

  const sortedRows = computed(() => sortRows(rows.value || [], sort.value))
  const visibleRows = computed(() => sortedRows.value.slice(0, visibleCount.value))
  const hasMore = computed(() => visibleCount.value < sortedRows.value.length)

  function toggleSort(sortKey) {
    const isSameColumn = sort.value.key === sortKey
    sort.value = {
      key: sortKey,
      direction: isSameColumn && sort.value.direction === SORT_DIRECTIONS.ASC
        ? SORT_DIRECTIONS.DESC
        : SORT_DIRECTIONS.ASC
    }
  }

  function isSortedBy(column) {
    return Boolean(column.sortKey) && sort.value.key === column.sortKey
  }

  function sortIconClass(column) {
    if (!isSortedBy(column)) return 'bi-arrow-down-up'
    // Up when values grow down the column, down when they shrink.
    return sort.value.direction === SORT_DIRECTIONS.ASC ? 'bi-sort-up' : 'bi-sort-down'
  }

  function ariaSort(column) {
    if (!column.sortKey) return undefined
    if (!isSortedBy(column)) return 'none'
    return sort.value.direction === SORT_DIRECTIONS.ASC ? 'ascending' : 'descending'
  }

  function loadMore() {
    visibleCount.value += PAGE_SIZE
  }

  return {
    sortedRows,
    visibleRows,
    hasMore,
    toggleSort,
    isSortedBy,
    sortIconClass,
    ariaSort,
    loadMore
  }
}
